import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/EnterpriseAuthContext";
import { InkyWorking } from "./brand";
import { AuthLayout, AuthCard } from "./pagekit";

const CompleteProfile = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Get location to read URL hash
  const { state: authState, actions: authActions } = useAuth();
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [linkError, setLinkError] = useState(null); // State for link-specific errors

  // Establish a Supabase session from recovery parameters
  useEffect(() => {
    let isMounted = true;

    const ensureRecoverySession = async () => {
      if (linkError) return;

      const hashParams = new URLSearchParams(
        (location.hash || "").replace(/^#/, "")
      );
      const searchParams = new URLSearchParams(window.location.search);
      const getParam = (name) => hashParams.get(name) || searchParams.get(name);
      const code = getParam("code");
      const accessToken = getParam("access_token");
      const refreshToken = getParam("refresh_token");
      const type = getParam("type");
      const normalizedType = (type || "").toLowerCase();
      const isRecoveryLike = ["recovery", "invite", "signup"].includes(
        normalizedType
      );

      if (!code && !(isRecoveryLike && accessToken && refreshToken)) {
        return;
      }

      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (isRecoveryLike) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
        }

        if (window.location.hash) {
          window.history.replaceState(
            {},
            document.title,
            `${window.location.pathname}${window.location.search}`
          );
        }
      } catch (sessionError) {
        if (!isMounted) return;
        setLinkError(
          sessionError.message ||
            "Your invitation link has expired or is invalid."
        );
      }
    };

    ensureRecoverySession();

    return () => {
      isMounted = false;
    };
  }, [location, linkError]);

  // Check for errors in the URL hash on component mount
  useEffect(() => {
    if (location.hash) {
      const params = new URLSearchParams(location.hash.substring(1)); // remove #
      const errorCode = params.get("error_code");
      const errorDescription = params.get("error_description");

      if (errorCode === "otp_expired") {
        setLinkError(errorDescription || "Your invitation link has expired.");
      } else if (errorCode) {
        setLinkError(errorDescription || "An unknown error occurred with the link.");
      }
    }
  }, [location]);

  // Use useEffect to react to changes in the authenticated user state
  useEffect(() => {
    // If the auth context has a user, pre-fill the username
    if (authState.user) {
      // Use username from profile, fallback to splitting email
      const initialUsername = authState.user.profile?.username || authState.user.username || authState.user.email.split('@')[0];
      setUsername(initialUsername);
    }
  }, [authState.user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }
    if (!username.trim()) {
      setError("Username is required.");
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
        data: { username: username.trim() },
      });

      if (updateError) throw updateError;

      // Also update the public profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ username: username.trim() })
        .eq('id', authState.user.id); // Use authState.user.id
      
      if (profileError) throw profileError;

      // Make sure the invited membership is active. The server activates it when the invite
      // email is sent; accept_invite is the fallback and only works for server-sent invites.
      // Clients cannot write memberships directly.
      // invite-user stores the invite in app_metadata (server-written); user_metadata is legacy.
      const invitedToAccount =
        authState.user?.app_metadata?.invited_to_account || authState.user?.user_metadata?.invited_to_account;
      if (invitedToAccount) {
        const { error: acceptError } = await supabase.rpc('accept_invite', {
          p_account: invitedToAccount,
        });
        // Not fatal: the password is already set, and older databases without the RPC
        // activate invites in the signup trigger.
        if (acceptError) console.warn('Could not confirm the invite membership:', acceptError.message);

        // Refresh the user's account data to reflect the new active status
        await authActions.refreshAccountData();
      }


      setSuccess("Your account has been set up successfully! Redirecting...");
      setTimeout(() => {
        navigate("/projects");
      }, 2000);

    } catch (err) {
      setError(err.message || "An error occurred during profile completion.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthCard
        title="Complete your profile"
        subtitle={linkError || !authState.user ? undefined : "Welcome to Quill AI. Choose a username and password to secure your account."}
      >
        {/* Handle Link Error State */}
        {linkError ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>{linkError}</Alert>
            <Typography variant="body2" color="text.secondary">
              Go back to the{" "}
              <Link component={RouterLink} to="/login">
                sign-in page
              </Link>{" "}
              and use "Forgot password?" to get a new link by email.
            </Typography>
          </Box>
        ) : authState.loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }} role="status">
            <InkyWorking label="Finalizing your session" mood="thinking" />
          </Box>
        ) : !authState.user ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              Your sign-in session is missing or has expired.
            </Alert>
            <Button variant="contained" fullWidth component={RouterLink} to="/login">
              Back to sign in
            </Button>
          </Box>
        ) : success ? (
          <Alert severity="success" role="status">{success}</Alert>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                required
                autoFocus
                autoComplete="username"
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                autoComplete="new-password"
                helperText="At least 6 characters."
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ minHeight: 44 }}
              >
                {loading ? "Saving..." : "Save and continue"}
              </Button>
            </Box>
          </>
        )}
      </AuthCard>
    </AuthLayout>
  );
};

export default CompleteProfile;
